import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ContactosInbox.css';
import { apiFetch } from '../../../services/dashboardApi';

function ContactosInbox({ token, canAccess }) {
    const [mensajesContacto, setMensajesContacto] = useState([]);
    const [solicitudesRegistroSocio, setSolicitudesRegistroSocio] = useState([]);
    const [solicitudesEventos, setSolicitudesEventos] = useState([]);
    const [tipoMensaje, setTipoMensaje] = useState('contacto');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ field: 'created_at', direction: 'desc' });

    const getCurrentData = useCallback(() => {
        if (tipoMensaje === 'eventos') return solicitudesEventos;
        if (tipoMensaje === 'registro-socio') return solicitudesRegistroSocio;
        return mensajesContacto;
    }, [tipoMensaje, solicitudesEventos, solicitudesRegistroSocio, mensajesContacto]);

    const filtered = useMemo(() => {
        const data = getCurrentData();
        const list = !filter.trim()
            ? data
            : data.filter((item) => {
                if (tipoMensaje === 'eventos') {
                    return [item.nombre, item.correo, item.evento_titulo, item.mensaje]
                        .filter(Boolean)
                        .some((field) => field.toLowerCase().includes(filter.toLowerCase()));
                }
                return [item.nombre, item.correo, item.motivo, item.mensaje]
                    .filter(Boolean)
                    .some((field) => field.toLowerCase().includes(filter.toLowerCase()));
            });

        const { field, direction } = sortConfig;
        return [...list].sort((a, b) => {
            const dir = direction === 'asc' ? 1 : -1;
            if (field === 'created_at') {
                const da = new Date(a.created_at || 0).getTime();
                const db = new Date(b.created_at || 0).getTime();
                return (da - db) * dir;
            }
            const va = (a[field] || '').toString().toLowerCase();
            const vb = (b[field] || '').toString().toLowerCase();
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [getCurrentData, tipoMensaje, filter, sortConfig]);

    const fetchBandejaContacto = async () => {
        const response = await apiFetch('http://localhost:8000/api/contacto/bandeja/', {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.detail || 'No se pudo cargar la bandeja de contacto');
        }

        setMensajesContacto(Array.isArray(data.mensajes_de_contacto) ? data.mensajes_de_contacto : []);
        setSolicitudesRegistroSocio(Array.isArray(data.solicitudes_registro_socio) ? data.solicitudes_registro_socio : []);
    };

    const fetchSolicitudesEventos = async () => {
        const response = await apiFetch('http://localhost:8000/api/solicitudes-eventos/', {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.detail || 'No se pudo cargar las solicitudes de eventos');
        }

        const lista = Array.isArray(data) ? data : data.results || data;
        setSolicitudesEventos(Array.isArray(lista) ? lista : []);
    };

    const fetchTodos = async () => {
        if (!canAccess) return;
        setLoading(true);
        setError('');
        try {
            await Promise.all([fetchBandejaContacto(), fetchSolicitudesEventos()]);
        } catch (err) {
            setError(err.message || 'No se pudo cargar la bandeja');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTodos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAccess]);

    const formatFecha = (fechaIso) => {
        if (!fechaIso) return '-';
        return new Date(fechaIso).toLocaleString();
    };

    const handleSort = (field) => {
        setSortConfig((prev) => {
            if (prev.field === field) {
                return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { field, direction: 'asc' };
        });
    };

    const sortIndicator = (field) => {
        if (sortConfig.field !== field) return '';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const toggleEstado = async (id, campo) => {
        const esEvento = tipoMensaje === 'eventos';
        const endpoint = esEvento
            ? `http://localhost:8000/api/solicitudes-eventos/${id}/`
            : `http://localhost:8000/api/contacto/${id}/`;

        const collection = esEvento ? solicitudesEventos : getCurrentData();
        const item = collection.find((row) => row.id === id);
        if (!item) return;

        const siguiente = !item[campo];

        try {
            const response = await apiFetch(endpoint, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ [campo]: siguiente }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'No se pudo actualizar el estado');
            }

            if (esEvento) {
                setSolicitudesEventos((prev) => prev.map((row) => (row.id === id ? { ...row, [campo]: siguiente } : row)));
            } else if (tipoMensaje === 'registro-socio') {
                setSolicitudesRegistroSocio((prev) => prev.map((row) => (row.id === id ? { ...row, [campo]: siguiente } : row)));
            } else {
                setMensajesContacto((prev) => prev.map((row) => (row.id === id ? { ...row, [campo]: siguiente } : row)));
            }
        } catch (err) {
            alert(err.message);
            fetchTodos();
        }
    };

    if (!canAccess) {
        return <p>No tienes permisos para ver los mensajes de contacto.</p>;
    }

    return (
        <div className="inbox-container">
            <div className="inbox-header">
                <div className="inbox-header-info">
                    <h3 className="inbox-title">Bandeja de Mensajes</h3>
                    <p className="inbox-subtitle">
                        Solo visible para administradores y secretarias. Puedes ordenar clicando los encabezados y marcar leído o respondido.
                    </p>
                </div>
                <button onClick={fetchTodos} disabled={loading} className="btn btn-primary">
                    {loading ? 'Actualizando...' : 'Refrescar'}
                </button>
            </div>

            <div className="inbox-tabs">
                <button
                    onClick={() => setTipoMensaje('contacto')}
                    className={`tab-btn ${tipoMensaje === 'contacto' ? 'active' : ''}`}
                >
                    📧 Mensajes de Contacto ({mensajesContacto.length})
                </button>
                <button
                    onClick={() => setTipoMensaje('registro-socio')}
                    className={`tab-btn ${tipoMensaje === 'registro-socio' ? 'active' : ''}`}
                >
                    👥 Solicitud Registro Socio ({solicitudesRegistroSocio.length})
                </button>
                <button
                    onClick={() => setTipoMensaje('eventos')}
                    className={`tab-btn ${tipoMensaje === 'eventos' ? 'active' : ''}`}
                >
                    📅 Solicitudes de Eventos ({solicitudesEventos.length})
                </button>
            </div>

            <div className="search-wrapper">
                <input
                    type="text"
                    placeholder={tipoMensaje === 'eventos'
                        ? 'Buscar por nombre, correo, evento o mensaje'
                        : 'Buscar por nombre, correo, motivo o mensaje'}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="search-input"
                />
            </div>

            {error && <div className="error-box">{error}</div>}

            <div className="table-responsive">
                {tipoMensaje === 'eventos' ? (
                    <table className="inbox-table">
                        <thead>
                            <tr className="thead-row">
                                <th className="th-sortable" onClick={() => handleSort('created_at')}>Fecha{sortIndicator('created_at')}</th>
                                <th className="th-sortable" onClick={() => handleSort('evento_titulo')}>Evento{sortIndicator('evento_titulo')}</th>
                                <th className="th-sortable" onClick={() => handleSort('nombre')}>Nombre{sortIndicator('nombre')}</th>
                                <th className="th-sortable" onClick={() => handleSort('correo')}>Correo{sortIndicator('correo')}</th>
                                <th className="th-sortable" onClick={() => handleSort('telefono')}>Teléfono{sortIndicator('telefono')}</th>
                                <th className="th">Mensaje</th>
                                <th className="th-sortable" onClick={() => handleSort('leido')}>Leído{sortIndicator('leido')}</th>
                                <th className="th-sortable" onClick={() => handleSort('respondido')}>Respondido{sortIndicator('respondido')}</th>
                                <th className="th">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan="9" className="td-center">Cargando…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan="9" className="td-center">No hay solicitudes de eventos.</td></tr>
                            )}
                            {!loading && filtered.map((solicitud) => (
                                <tr key={solicitud.id} className="tbody-row">
                                    <td className="td">{formatFecha(solicitud.created_at)}</td>
                                    <td className="td">
                                        <div>
                                            <strong>{solicitud.evento_titulo}</strong>
                                            {solicitud.evento_fecha && (
                                                <div className="event-date">{formatFecha(solicitud.evento_fecha)}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="td">{solicitud.nombre}</td>
                                    <td className="td"><a href={`mailto:${solicitud.correo}`} className="link-primary">{solicitud.correo}</a></td>
                                    <td className="td">{solicitud.telefono || '—'}</td>
                                    <td className="td message-cell"><div className="pre-wrap">{solicitud.mensaje}</div></td>
                                    <td className="td"><span className={`badge ${solicitud.leido ? 'badge-success' : 'badge-warning'}`}>{solicitud.leido ? 'Leído' : 'Pendiente'}</span></td>
                                    <td className="td"><span className={`badge ${solicitud.respondido ? 'badge-success' : 'badge-warning'}`}>{solicitud.respondido ? 'Respondido' : 'Sin responder'}</span></td>
                                    <td className="td">
                                        <div className="actions-row">
                                            <button onClick={() => toggleEstado(solicitud.id, 'leido')} className={`btn-small ${solicitud.leido ? 'btn-muted' : 'btn-success'}`}>
                                                {solicitud.leido ? 'Marcar no leído' : 'Marcar leído'}
                                            </button>
                                            <button onClick={() => toggleEstado(solicitud.id, 'respondido')} className={`btn-small ${solicitud.respondido ? 'btn-muted' : 'btn-respond'}`}>
                                                {solicitud.respondido ? 'Marcar sin responder' : 'Marcar respondido'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="inbox-table">
                        <thead>
                            <tr className="thead-row">
                                <th className="th-sortable" onClick={() => handleSort('created_at')}>Fecha{sortIndicator('created_at')}</th>
                                <th className="th-sortable" onClick={() => handleSort('nombre')}>Nombre{sortIndicator('nombre')}</th>
                                <th className="th-sortable" onClick={() => handleSort('correo')}>Correo{sortIndicator('correo')}</th>
                                <th className="th-sortable" onClick={() => handleSort('telefono')}>Teléfono{sortIndicator('telefono')}</th>
                                <th className="th-sortable" onClick={() => handleSort('motivo')}>Motivo{sortIndicator('motivo')}</th>
                                <th className="th">Mensaje</th>
                                <th className="th-sortable" onClick={() => handleSort('leido')}>Leído{sortIndicator('leido')}</th>
                                <th className="th-sortable" onClick={() => handleSort('respondido')}>Respondido{sortIndicator('respondido')}</th>
                                <th className="th">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan="9" className="td-center">Cargando…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="td-center">
                                        {tipoMensaje === 'registro-socio'
                                            ? 'No hay solicitudes de registro de socio.'
                                            : 'No hay mensajes de contacto.'}
                                    </td>
                                </tr>
                            )}
                            {!loading && filtered.map((contacto) => (
                                <tr key={contacto.id} className="tbody-row">
                                    <td className="td">{formatFecha(contacto.created_at)}</td>
                                    <td className="td">{contacto.nombre}</td>
                                    <td className="td"><a href={`mailto:${contacto.correo}`} className="link-primary">{contacto.correo}</a></td>
                                    <td className="td">{contacto.telefono || '—'}</td>
                                    <td className="td">{contacto.motivo}</td>
                                    <td className="td message-cell"><div className="pre-wrap">{contacto.mensaje}</div></td>
                                    <td className="td"><span className={`badge ${contacto.leido ? 'badge-success' : 'badge-warning'}`}>{contacto.leido ? 'Leído' : 'Pendiente'}</span></td>
                                    <td className="td"><span className={`badge ${contacto.respondido ? 'badge-success' : 'badge-warning'}`}>{contacto.respondido ? 'Respondido' : 'Sin responder'}</span></td>
                                    <td className="td">
                                        <div className="actions-row">
                                            <button onClick={() => toggleEstado(contacto.id, 'leido')} className={`btn-small ${contacto.leido ? 'btn-muted' : 'btn-success'}`}>
                                                {contacto.leido ? 'Marcar no leído' : 'Marcar leído'}
                                            </button>
                                            <button onClick={() => toggleEstado(contacto.id, 'respondido')} className={`btn-small ${contacto.respondido ? 'btn-muted' : 'btn-respond'}`}>
                                                {contacto.respondido ? 'Marcar sin responder' : 'Marcar respondido'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default ContactosInbox;
